import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import pluralize from 'pluralize';
import { Observable, of as observableOf } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { FAKE_EVENT_FIELD } from '../../../../../shared/fields/fake-fields';
import { STANDARD_FIELDS } from '../../../../../shared/fields/standard-fields';
import * as eventActions from '../../../core/store/events/event.actions';
import { Field, FieldWithDepartmentField } from '../../../core/store/fields/field.model';
import { getSelectedGroupIds, getSelectedStudioGroupIds, getSelectedSubGroupIds } from '../../../core/store/groups/group-utils';
import * as layoutActions from '../../../core/store/layout/layout.actions';
import {
    Asset,
    AssetEvent,
    Character,
    Department,
    DepartmentField,
    Disposition,
    Division,
    Event,
    Franchise,
    Group,
    Set,
    Status,
    SubDepartment,
    Transaction
} from '../../../core/store/models';
import { rootSelectors, subDepartmentSelectors } from '../../../core/store/selectors';
import { ActionStatus, SavingMessage, Severity, TableTypes } from '../../../core/store/types';
import { buildPDropdownOptions } from '../../../core/store/utils';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { PDropdownOption } from '../../interfaces/p-dropdown-options';
import { SaveAssetsOptions } from './../../../shared/data-services/asset-data.service';
import { UPDATE_LIMIT } from '../../../../../shared/constants/asset-limit.constant';
import * as departmentActions from '../../../core/store/departments/department.actions';

export const BULK_MODAL_ACTIONS = {
    DISPOSITION: 'update_disposition',
    EXCLUDED_DISPOSITION: 'update_excluded_disposition',
    DEPARTMENT: 'update_department',
    GROUPS: 'update_groups',
    STATUS: 'update_status',
    EVENT: 'update_event',
    CHARACTER: 'update_character',
    SET: 'update_set',
    REMOVE: 'remove',
    TRANSFER: 'transfer',
    COPY_EVENT: 'copy_event',
    ADD_GROUP_OR_SUBGROUP_ASSETS_TO_EVENT: 'add_group_or_subgroup_assets_to_event',
};

export interface BulkModalData {
    action: string;
    allGroups$?: Observable<Group[]>;
    models: any[];
    sourceEventId?: number;
    targetEventId?: number;
    bulkSave?: (models: Asset[] | Transaction[], dialogRef: MatDialogRef<BulkModalComponent>) => void;
    bulkUpdate?: (models: Asset[] | Transaction[], dialogRef: MatDialogRef<BulkModalComponent>, fieldValue: any) => void;
    bulkAddToEvent?: (models: Asset[] | Transaction[], entity: number, dialogRef: MatDialogRef<BulkModalComponent>) => void;
    transferAssetsToEvent?: (assetEvents: AssetEvent[], sourceEventId: number, targetEventId: number, dialogRef: MatDialogRef<any>) => void;
    confirm?: boolean;
    divisionGroups$?: Observable<Group[]>;
    notDivisionGroups$?: Observable<Group[]>;
    bulkAddGroupstoAssets?: (assets: Asset[], groupIds: number[], subgroupIds: number[], dialogRef: MatDialogRef<BulkModalComponent>) => void;
    bulkRemoveAssetsFromGroup?: (assets: Asset[], groupId: number, dialogRef: MatDialogRef<BulkModalComponent>) => void;
    bulkRemoveAssetsFromSubGroup?: (assets: Asset[], subGroupId: number, dialogRef: MatDialogRef<BulkModalComponent>) => void;
    bulkAddGroupOrSubGroupAssetsToEvent?: (groupId: number, subGroupId: number, eventId: number, dialogRef: MatDialogRef<BulkModalComponent>) => void;
    groupId?: number;
    subGroupId?: number;
    clearSelectedAssets?: () => void;
    clearSelectedCharacter?: () => void;
    clearSelectedEvent?: () => void;
    clearSelectedSet?: () => void;
    characters?: Observable<Character[]>;
    fields: Field[];
    franchise?: Franchise;
    franchiseGroups$?: Observable<Group[]>;
    notFranchiseGroups$?: Observable<Group[]>;
    dispositions?: Observable<Disposition[]>;
    excludedDispositions?: Observable<Disposition[]>;
    departments?: Observable<Department[]>;
    division?: Division;
    events?: Observable<Event[]>;
    isTransactionUpdate?: boolean;
    modalText?: string;
    numOfDivisionAssets?: number;
    numOfFranchiseAssets?: number;
    numOfNonDivisionAssets?: number;
    sets?: Observable<Set[]>;
    statuses?: Observable<Status[]>;
    studioId?: number;
    subDepartments?: Observable<SubDepartment[]>;
    selectCharacter?: (characterId: number) => void;
    selectSet?: (setId: number) => void;
    tableType?: TableTypes;
}

@Component({
    templateUrl: './bulk-modal.component.html',
    styles: [`
        .create-form {
            margin-top: 7px;
        }
        :host ::ng-deep .p-dropdown {
            width: 100%;
        }
        :host ::ng-deep .mdc-dialog__title+.mdc-dialog__content {
            padding: 24px;
        }
    `],
    standalone: false
})
export class BulkModalComponent implements OnInit {
    action: string;
    allGroups$: Observable<Group[]> = observableOf([]);
    departments: Department[];
    subDepartment: SubDepartment[];
    models: any[];
    modelForm: UntypedFormGroup;
    sourceEventId: number;
    targetEventId: number;
    bulkSave: (models: Asset[] | Transaction[], dialogRef: MatDialogRef<BulkModalComponent>, model?: object) => void;
    bulkUpdate: (models: Asset[] | Transaction[], dialogRef: MatDialogRef<BulkModalComponent>, fieldValue: any, overwrite: boolean) => void;
    bulkAddToEvent: (models: Asset[] | Transaction[], entity: number, dialogRef: MatDialogRef<BulkModalComponent>, options?: SaveAssetsOptions) => void;
    bulkAddGroupstoAssets: (assets: Asset[], groupIds: number[], subgroupIds: number[], dialogRef: MatDialogRef<BulkModalComponent>) => void;
    bulkRemoveAssetsFromGroup: (assets: Asset[], groupId: number, dialogRef: MatDialogRef<BulkModalComponent>) => void;
    bulkRemoveAssetsFromSubGroup: (assets: Asset[], subGroupId: number, dialogRef: MatDialogRef<BulkModalComponent>) => void;
    transferAssetsToEvent: (assetEvents: AssetEvent[], sourceEventId: number, targetEventId: number, dialogRef: MatDialogRef<any>) => void;
    bulkAddGroupOrSubGroupAssetsToEvent: (groupId: number, subGroupId: number, sourceEventId: number, dialogRef: MatDialogRef<BulkModalComponent>) => void;

    groupId: number;
    subGroupId: number;
    clearSelectedAssets: () => void;
    clearSelectedEvent: () => void;
    clearSelectedCharacter: () => void;
    clearSelectedSet: () => void;
    confirm = false;
    dispositionOptions: Observable<PDropdownOption[]> = observableOf([]);
    excludedDispositionOptions: Observable<PDropdownOption[]> = observableOf([]);
    departmentOptions: Observable<PDropdownOption[]> = observableOf([]);
    divisionGroups$: Observable<Group[]> = observableOf([]);
    notDivisionGroups$: Observable<Group[]> = observableOf([]);
    franchise?: Franchise;
    franchiseGroups$: Observable<Group[]> = observableOf([]);
    notFranchiseGroups$: Observable<Group[]> = observableOf([]);
    franchiseOptions: PDropdownOption[];
    division?: Division;
    characterOptions: Observable<PDropdownOption[]> = observableOf([]);
    selectedGroups: string[] = [];
    selectedSubGroups: string[] = [];
    setOptions: Observable<PDropdownOption[]> = observableOf([]);
    eventOptions: Observable<PDropdownOption[]> = observableOf([]);
    isTransactionUpdate = false;
    modalText = '';
    savingMessage: SavingMessage[] = [];
    statusOptions: Observable<PDropdownOption[]> = observableOf([]);
    subDepartments: Observable<SubDepartment[]> = observableOf([]);
    subDepartmentOptions: Observable<PDropdownOption[]> = observableOf([]);
    fields: FieldWithDepartmentField[];
    isGroupField = false;
    saveDisabled = false; // Set in transaction-data.service.ts and asset-data.service.ts
    standardFields = STANDARD_FIELDS;
    studioId: number;
    eventField = FAKE_EVENT_FIELD;
    groupField = STANDARD_FIELDS.group_ids;
    title: string;
    notRequiredFields = ['status_id', 'character_id', 'set_id', 'sub_department_id'];
    nullableFields = ['status_id', 'character_id', 'set_id', 'sub_department_id'];
    BULK_MODAL_ACTIONS = BULK_MODAL_ACTIONS;
    updateLimit = UPDATE_LIMIT;
    tableType: number;
    isDivisionTable: boolean;
    isFranchiseTable: boolean;

    constructor(@Inject(MAT_DIALOG_DATA) private data: BulkModalData, public dialog: MatDialog, public dialogRef: MatDialogRef<BulkModalComponent>, private fb: UntypedFormBuilder, private store: Store<rootSelectors.State>) { }

    ngOnInit() {
        this.bulkSave = this.data.bulkSave;
        this.bulkUpdate = this.data.bulkUpdate;
        this.bulkAddToEvent = this.data.bulkAddToEvent;
        this.bulkAddGroupstoAssets = this.data.bulkAddGroupstoAssets;
        this.bulkRemoveAssetsFromGroup = this.data.bulkRemoveAssetsFromGroup;
        this.bulkRemoveAssetsFromSubGroup = this.data.bulkRemoveAssetsFromSubGroup;
        this.transferAssetsToEvent = this.data.transferAssetsToEvent;
        this.groupId = this.data.groupId;
        this.subGroupId = this.data.subGroupId;
        this.confirm = !!this.data.confirm;
        this.models = this.data.models;
        this.action = this.data.action;
        this.modalText = this.data.modalText;
        this.isTransactionUpdate = this.data.isTransactionUpdate ? true : false;
        this.sourceEventId = this.data.sourceEventId;
        this.targetEventId = this.data.targetEventId;
        this.bulkAddGroupOrSubGroupAssetsToEvent = this.data.bulkAddGroupOrSubGroupAssetsToEvent;



        switch (this.action) {
            case BULK_MODAL_ACTIONS.EXCLUDED_DISPOSITION:
                this.title = `Bulk Update Excluded Dispositions`;
                break;
            case BULK_MODAL_ACTIONS.DISPOSITION:
                this.title = `Bulk Update Dispositions`;
                break;
            case BULK_MODAL_ACTIONS.DEPARTMENT:
                this.title = `Bulk Update Departments`;
                break;
            case BULK_MODAL_ACTIONS.GROUPS:
                this.title = `Group Assets`;
                break;
            case BULK_MODAL_ACTIONS.STATUS:
                this.title = `Bulk Update Status`;
                break;
            case BULK_MODAL_ACTIONS.EVENT:
                this.title = `Bulk Update Event`;
                break;
            case BULK_MODAL_ACTIONS.CHARACTER:
                this.title = `Bulk Update Character`;
                break;
            case BULK_MODAL_ACTIONS.SET:
                this.title = `Bulk Update Set`;
                break;
            case BULK_MODAL_ACTIONS.REMOVE:
                this.title = `Remove Assets`;
                break;
            case BULK_MODAL_ACTIONS.TRANSFER:
                this.title = `Transfer To Event`;
                break;
            case BULK_MODAL_ACTIONS.COPY_EVENT:
                this.title = `Copy To Event`;
                break;
            case BULK_MODAL_ACTIONS.ADD_GROUP_OR_SUBGROUP_ASSETS_TO_EVENT:
                this.title = `Add ${this.groupId ? 'Group' : 'SubGroup'} Assets to Event`;
                break;
        }

        this.fields = this.data.fields.map(field => {
            if (field.canonical_name === this.groupField.canonical_name) {
                this.isGroupField = true;
            }
            return { field, departmentField: new DepartmentField({ required: false }) };
        });

        if (this.data.dispositions) {
            this.dispositionOptions = this.data.dispositions.pipe(
                map(dispositions => buildPDropdownOptions(dispositions)));
        }
        if (this.data.excludedDispositions) {
            this.excludedDispositionOptions = this.data.excludedDispositions.pipe(
                map(dispositions => buildPDropdownOptions(dispositions)));
        }
        if (this.data.departments) {
            this.departmentOptions = this.data.departments.pipe(
                map(departments => departments.map(dept => {
                    return { ...dept, ...{ name: dept.account_code + ' ' + dept.name } };
                })),
                map(departments => buildPDropdownOptions(departments)));
        }
        this.allGroups$ = this.data.allGroups$;
        this.divisionGroups$ = this.data.divisionGroups$;
        this.notDivisionGroups$ = this.data.notDivisionGroups$;
        this.franchiseGroups$ = this.data.franchiseGroups$;
        this.notFranchiseGroups$ = this.data.notFranchiseGroups$;
        this.studioId = this.data.studioId;
        if (this.data.statuses) {
            this.statusOptions = this.data.statuses.pipe(
                map(statuses => buildPDropdownOptions(statuses, {
                    nullOption: true,
                    nullLabel: 'Status'
                })));
        }
        if (this.data.characters) {
            this.characterOptions = this.data.characters.pipe(
                map(characters => buildPDropdownOptions(characters)));
        }
        if (this.data.sets) {
            this.setOptions = this.data.sets.pipe(
                map(sets => buildPDropdownOptions(sets)));
        }
        if (this.data.events) {
            this.eventOptions = this.data.events.pipe(
                map(events => buildPDropdownOptions(events, {
                    nullOption: true,
                    nullLabel: 'Event',
                    sortLabels: 1
                })));
        }
        if (this.data.franchise) {
            this.franchise = this.data.franchise;
        }
        if (this.data.division) {
            this.division = this.data.division;
        }
        if (this.data.clearSelectedAssets) {
            this.clearSelectedAssets = this.data.clearSelectedAssets;
        }
        if (this.data.clearSelectedEvent) {
            this.clearSelectedEvent = this.data.clearSelectedEvent;
        }
        if (this.data.clearSelectedCharacter) {
            this.clearSelectedCharacter = this.data.clearSelectedCharacter;
        }
        if (this.data.clearSelectedSet) {
            this.clearSelectedSet = this.data.clearSelectedSet;
        }
        this.tableType = this.data.tableType
        this.buildForm();

        this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
            // Call functions to clear all selected entities so if we re-open the modal we don't have anything selected
            const clearFunctions = [
                this.clearSelectedEvent
            ];
            clearFunctions.forEach(func => func && func());
        });
        this.isDivisionTable = (this.tableType === TableTypes.DivisionTable || this.tableType === TableTypes.DivisionGroupTable || this.tableType === TableTypes.EventTable || this.tableType === TableTypes.DivisionSubGroupTable) ? true : false;
        this.isFranchiseTable = (this.tableType === TableTypes.FranchiseTable || this.tableType === TableTypes.FranchiseGroupTable || this.tableType === TableTypes.FranchiseSubGroupTable) ? true : false;
    }

    buildForm() {
        // Build reactive form from fields
        const formObj = {};
        this.fields.forEach(field => {
            const validators = field.field.getValidators(!this.notRequiredFields.includes(field.field.canonical_name));
            let initial;
            if (this.nullableFields.includes(field.field.canonical_name)) {
                initial = { value: null, disabled: false };
            } else {
                initial = { value: '', disabled: false };
            }
            formObj[field.field.canonical_name] = [initial, validators];
        });
        this.modelForm = this.fb.group(formObj);
    }

    openAssetOverlapWarning(numOverlapAssets) {
        this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Warning: Overlapping Assets in Events',
                message: `${numOverlapAssets} Assets in the selected Event will overlap with other Events in this Division. Are you sure you want to add these Assets to the Event?`,
                confirmButton: 'Save',
                confirmColor: 'warn'
            }
        }).afterClosed().pipe(take(1)).subscribe(save => {
            if (save) {
                this.bulkAddToEvent(this.models, this.modelForm.value.event_id, this.dialogRef);
            } else {
                this.savingMessage = [];
            }
        });
    }

    addAssetsToEvent() {
        const assetIds = this.models.map((asset) => asset.id);
        let selectedEvent = null;

        this.data.events.pipe(take(1)).subscribe((events) => {
            const currentEvent = events.find((ev) => ev.id === this.modelForm.value.event_id);
            selectedEvent = new Event(currentEvent);
            selectedEvent.attachments = [];
            delete selectedEvent.loan_venue_name;

            const invalidAssets = this.models.filter((asset) => asset.division_id !== selectedEvent.division_id);
            if (invalidAssets.length > 0) {
                this.savingMessage = [{ severity: Severity.Error, summary: `Assets must be in same division as event.` }];
                return;
            }

            this.store.dispatch(new eventActions.GetOverlapAssetsAction({ event: selectedEvent, assetIds }));
        });

        this.store.select(rootSelectors.getEventState).pipe(
            filter((state) => state.gettingOverlap !== ActionStatus.Inactive && state.gettingOverlap !== ActionStatus.Loading),
            take(1))
            .subscribe(state => {
                const actionStatus = state.gettingOverlap;
                const numOverlapAssets = state.numOverlapAssets;
                if (actionStatus === ActionStatus.Complete && numOverlapAssets) {
                    this.openAssetOverlapWarning(numOverlapAssets);
                } else if (actionStatus === ActionStatus.Complete) {
                    const actionMessage = this.action === BULK_MODAL_ACTIONS.COPY_EVENT ? `${assetIds.length} ${pluralize('Asset', assetIds.length)} copied to "${selectedEvent.name}"` : `Added ${assetIds.length} ${pluralize('Asset', assetIds.length)} to "${selectedEvent.name}"`;
                    const infoAction = new layoutActions.InfoAction({ message: actionMessage });
                    this.bulkAddToEvent(this.models, this.modelForm.value.event_id, this.dialogRef, { infoAction } as SaveAssetsOptions);
                }
                this.store.dispatch(new eventActions.SetOverlapAssetsInactive());
            });

    }

    addGroupsToAssets() {
        let selectedGroupIds = [];
        let selectedSubgropuIds = [];
        if (this.division || this.franchise) {
            selectedGroupIds = getSelectedGroupIds(this.divisionGroups$, this.notDivisionGroups$, this.franchiseGroups$, this.notFranchiseGroups$, this.isDivisionTable, this.isFranchiseTable, this.selectedGroups, this.allGroups$);
            selectedSubgropuIds = getSelectedSubGroupIds(this.divisionGroups$, this.notDivisionGroups$, this.franchiseGroups$, this.notFranchiseGroups$, this.isDivisionTable, this.isFranchiseTable, this.selectedSubGroups, this.allGroups$);
        } else {
            selectedGroupIds = getSelectedStudioGroupIds(this.allGroups$, this.selectedGroups);
            selectedSubgropuIds = getSelectedSubGroupIds(this.divisionGroups$, this.notDivisionGroups$, this.franchiseGroups$, this.notFranchiseGroups$, this.isDivisionTable, this.isFranchiseTable, this.selectedSubGroups, this.allGroups$);
        }

        this.bulkAddGroupstoAssets(this.models, selectedGroupIds, selectedSubgropuIds, this.dialogRef);
    }

    save() {
        if (this.action === BULK_MODAL_ACTIONS.REMOVE) {
            this.savingMessage = [{ severity: Severity.Info, summary: 'Removing...' }];
        } else {
            this.savingMessage = [{ severity: Severity.Info, summary: 'Saving...' }];
        }
        if (this.action === BULK_MODAL_ACTIONS.EVENT || this.action === BULK_MODAL_ACTIONS.COPY_EVENT) {
            this.addAssetsToEvent();
            return;
        }
        if (this.action === BULK_MODAL_ACTIONS.GROUPS) {
            this.addGroupsToAssets();
            return;
        }
        if (this.action === BULK_MODAL_ACTIONS.REMOVE) {
            if (this.groupId) {
                this.bulkRemoveAssetsFromGroup(this.models, this.groupId, this.dialogRef);
            }
            if (this.subGroupId) {
                this.bulkRemoveAssetsFromSubGroup(this.models, this.subGroupId, this.dialogRef);
            }
            return;
        }
        if (this.action === BULK_MODAL_ACTIONS.TRANSFER) {
            this.transferAssetsToEvent(this.models, this.sourceEventId, this.modelForm.value.event_id, this.dialogRef);
            return;
        }

        if (this.action === BULK_MODAL_ACTIONS.ADD_GROUP_OR_SUBGROUP_ASSETS_TO_EVENT) {
            this.bulkAddGroupOrSubGroupAssetsToEvent(this.groupId, this.subGroupId, this.modelForm.value.event_id, this.dialogRef)
            return;
        }

        const newModels = this.models.map(m => ({ ...m, ...this.modelForm.value }));

        if (this.data.numOfNonDivisionAssets > 0) {
            this.store.dispatch(new layoutActions.InfoAction({ message: `${this.data.numOfNonDivisionAssets} ${pluralize('Asset', this.data.numOfNonDivisionAssets)} could not be updated because ${pluralize('it', this.data.numOfNonDivisionAssets)} no longer ${pluralize('belong', this.data.numOfNonDivisionAssets)} to this division` }));

            if (!newModels.length) {
                this.dialogRef.close();
                return;
            }
        }

        if (this.data.numOfFranchiseAssets || this.data.numOfDivisionAssets) {
            if (!newModels.length) {
                this.dialogRef.close();
                return;
            }
        }

        if (this.modelForm.value.disposition_id === '') {
            this.savingMessage = [{ severity: Severity.Info, summary: 'Please select a disposition' }];
        } else if (this.modelForm.value.status_id === '') {
            this.savingMessage = [{ severity: Severity.Info, summary: 'Please select a status' }];
        } else {
            if (this.confirm) {
                this.dialog.open(ConfirmModalComponent, {
                    data: {
                        title: 'Warning',
                        message: `Updating ${newModels.length} assets cannot be undone. Are you sure you want to continue?`,
                        confirmButton: 'Confirm',
                        confirmColor: 'warn',
                        cancelButton: 'Cancel',
                        allowCancel: true,
                    }
                }).afterClosed().pipe(take(1)).subscribe(save => {
                    if (save) {
                        this.doSave(newModels, this.modelForm.value);
                    } else {
                        this.savingMessage = [];
                        return;
                    }
                });
            }
            else {
                this.doSave(newModels, this.modelForm.value);
            }
        }
    }

    doSave(models, model) {
        switch (this.action) {
            case BULK_MODAL_ACTIONS.CHARACTER:
            case BULK_MODAL_ACTIONS.DISPOSITION:
            case BULK_MODAL_ACTIONS.EXCLUDED_DISPOSITION:
            case BULK_MODAL_ACTIONS.SET:
            case BULK_MODAL_ACTIONS.STATUS:
                {
                    if (this.isTransactionUpdate) {
                        this.bulkSave(models, this.dialogRef, model);
                    } else {
                        this.bulkUpdate(models, this.dialogRef, this.modelForm.value, false);
                    }
                    break;
                }

            default:
                {
                    if (this.isTransactionUpdate) {
                        this.bulkSave(models, this.dialogRef, model);
                    } else {
                        this.bulkSave(models, this.modelForm.value, this.dialogRef);
                    }
                    break;
                }
        }
    }

    selectCharacter(characterId) {
        this.modelForm.value.character_id = characterId;
        if (this.data.selectCharacter) {
            this.data.selectCharacter(characterId);
        }
    }

    selectSet(setId) {
        this.modelForm.value.set_id = setId;
        if (this.data.selectSet) {
            this.data.selectSet(setId);
        }
    }

    selectDepartment(department_id: number) {
        this.store.dispatch(new departmentActions.SelectAction(department_id));
        this.subDepartments = this.store.select(subDepartmentSelectors.getSubDeptsForSelectedDept);
        this.subDepartmentOptions = this.subDepartments.pipe(
            take(1),
            map(subdepts => subdepts.map(subDept => {
                return {
                    ...subDept,
                    ...{ name: `${subDept.code} ${subDept.name}` }
                }
            })),
            map(subdepts => buildPDropdownOptions(subdepts, {
                nullOption: true,
                nullLabel: 'Sub Department'
            })));
    }
}
